import React, { useState, useEffect } from 'react';
import { fetchSpaces, sendMessageToSpace, sendDirectMessage } from '../services/chatService';
import { WeeklySummary } from '../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  accessToken: string;
  summary: WeeklySummary;
}

const ChatBroadcastModal: React.FC<Props> = ({ isOpen, onClose, accessToken, summary }) => {
  const [spaces, setSpaces] = useState<{name: string, displayName: string, type: string}[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState<string>('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [sendTasks, setSendTasks] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadSpaces();
      setSuccess(false);
      setError(null);
      setSearchQuery('');
      setIsDropdownOpen(false);
    }
  }, [isOpen]);

  const loadSpaces = async () => {
    setIsLoading(true);
    try {
      const fetchedSpaces = await fetchSpaces(accessToken);
      const filteredSpaces = fetchedSpaces.filter(s => 
        s.displayName !== 'Direct Message' && s.displayName !== 'Unnamed Space'
      );
      setSpaces(filteredSpaces);
      if (filteredSpaces.length > 0) {
        setSelectedSpace(filteredSpaces[0].name);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredSpacesList = spaces.filter(s => 
    s.displayName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleBroadcast = async () => {
    if (!selectedSpace) return;
    setIsSending(true);
    setError(null);
    
    try {
      // 1. Send broadcast to space
      const broadcastMessage = `*Executive Intelligence Overview*\n\n${summary.conciseOverview}\n\n*Key Outcomes:*\n${summary.keyOutcomes.map(o => `- ${o}`).join('\n')}`;
      await sendMessageToSpace(accessToken, selectedSpace, broadcastMessage);

      // 2. Send DMs to individuals
      if (sendTasks && summary.tasksPerPerson) {
        for (const taskGroup of summary.tasksPerPerson) {
          const person = taskGroup.name;
          const tasks = taskGroup.tasks;
          
          // Extract email if it's in the format "Name <email@example.com>" or just "email@example.com"
          const emailMatch = person.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/);
          const email = emailMatch ? emailMatch[1] : null;

          if (email) {
            const taskMessage = `*Your Tasks from Recent Meetings:*\n\n${tasks.map(t => `- ${t}`).join('\n')}`;
            try {
              await sendDirectMessage(accessToken, email, taskMessage);
            } catch (dmErr) {
              console.warn(`Failed to DM ${email}`, dmErr);
              // Don't fail the whole operation if one DM fails
            }
          }
        }
      }

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
          <h2 className="text-xl font-bold text-slate-800 flex items-center">
            <svg className="w-5 h-5 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            Broadcast to Chat
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-xl border border-red-100">
              {error}
            </div>
          )}

          {success ? (
            <div className="p-8 text-center space-y-4">
              <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-800">Broadcast Sent!</h3>
              <p className="text-slate-500">Overview posted to space and tasks routed to individuals.</p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <label className="block text-sm font-bold text-slate-700">Select Target Space</label>
                {isLoading ? (
                  <div className="h-10 bg-slate-100 animate-pulse rounded-xl"></div>
                ) : (
                  <div className="relative">
                    <div 
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500 transition-all flex items-center cursor-text"
                      onClick={() => setIsDropdownOpen(true)}
                    >
                      <input
                        type="text"
                        className="w-full bg-transparent outline-none text-sm font-medium text-slate-700 placeholder-slate-400"
                        placeholder="Search for a space..."
                        value={isDropdownOpen ? searchQuery : (spaces.find(s => s.name === selectedSpace)?.displayName || '')}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          setIsDropdownOpen(true);
                        }}
                        onFocus={() => setIsDropdownOpen(true)}
                        onBlur={() => {
                          // Delay closing to allow click on option
                          setTimeout(() => setIsDropdownOpen(false), 200);
                        }}
                      />
                      <svg className="w-4 h-4 text-slate-400 ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                    
                    {isDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                        {filteredSpacesList.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-slate-500">No spaces found.</div>
                        ) : (
                          filteredSpacesList.map(s => (
                            <div 
                              key={s.name}
                              className={`px-4 py-2.5 text-sm cursor-pointer hover:bg-indigo-50 transition-colors ${selectedSpace === s.name ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-slate-700'}`}
                              onClick={() => {
                                setSelectedSpace(s.name);
                                setSearchQuery('');
                                setIsDropdownOpen(false);
                              }}
                            >
                              {s.displayName}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-1">The concise overview and key outcomes will be posted here.</p>
              </div>

              <div className="flex items-start space-x-3 p-4 bg-slate-50 rounded-xl border border-slate-100">
                <div className="flex items-center h-5">
                  <input 
                    id="sendTasks" 
                    type="checkbox" 
                    checked={sendTasks}
                    onChange={(e) => setSendTasks(e.target.checked)}
                    className="w-4 h-4 text-indigo-600 bg-white border-slate-300 rounded focus:ring-indigo-500"
                  />
                </div>
                <div className="flex flex-col">
                  <label htmlFor="sendTasks" className="text-sm font-bold text-slate-700 cursor-pointer">
                    Route Tasks via Direct Message
                  </label>
                  <p className="text-xs text-slate-500 mt-1">
                    Automatically DM individuals their specific tasks (requires email addresses in the task list).
                  </p>
                </div>
              </div>

              <button
                onClick={handleBroadcast}
                disabled={isSending || !selectedSpace}
                className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded-xl font-bold shadow-md transition-all flex items-center justify-center space-x-2"
              >
                {isSending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Broadcasting...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    <span>Send Broadcast</span>
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatBroadcastModal;
